import React from 'react';
import { Users, Target, Award, Heart, Shield, Sparkles } from 'lucide-react';
import Header from './components/Header4';
import Footer from './components/Footer4';

const About = () => {
    const values = [
        { icon: Sparkles, title: 'Handcrafted Excellence', description: 'Every piece is meticulously crafted by skilled Indian artisans preserving centuries-old techniques.' },
        { icon: Heart, title: 'Sustainable Fashion', description: 'We support ethical practices, fair wages, and eco-friendly materials to protect our planet.' },
        { icon: Shield, title: 'Authenticity Guaranteed', description: 'Each product comes with a certificate of authenticity ensuring genuine craftsmanship.' },
        { icon: Users, title: 'Community First', description: 'We empower over 500 artisan families across India through fair trade partnerships.' },
    ];

    const stats = [
        { number: '10,000+', label: 'Happy Customers' },
        { number: '500+', label: 'Artisan Partners' },
        { number: '15+', label: 'States Covered' },
        { number: '5', label: 'Years of Trust' },
    ];

    return (
        <div className="w-full min-h-screen bg-white" style={{ fontFamily: "'Work Sans', sans-serif" }}>
            <Header />
            <link href="https://fonts.googleapis.com/css2?family=Work+Sans:wght@300;400;500;600;700&display=swap" rel="stylesheet" />

            <main className="pt-16 md:pt-20">
                {/* Hero */}
                <section className="relative w-full min-h-[50vh] md:min-h-[60vh] overflow-hidden">
                    <img
                        src="https://images.pexels.com/photos/2681751/pexels-photo-2681751.jpeg?auto=compress&cs=tinysrgb&w=1200"
                        alt="About NEELA"
                        className="absolute inset-0 w-full h-full object-cover"
                    />
                    <div className="absolute inset-0" style={{ backgroundColor: 'rgba(27, 73, 101, 0.75)' }} />
                    <div className="relative z-10 h-full min-h-[50vh] md:min-h-[60vh] flex items-center justify-center px-4 text-center">
                        <div className="max-w-2xl">
                            <p className="text-xs tracking-[0.2em] uppercase mb-3 text-[#62B6CB] font-medium">Our Story</p>
                            <h1 className="text-3xl md:text-4xl lg:text-5xl font-semibold text-white mb-4">About NEELA</h1>
                            <p className="text-sm md:text-base text-white/80 font-light leading-relaxed">
                                Born from a passion for Indian textiles, NEELA bridges the gap between traditional craftsmanship and contemporary fashion.
                            </p>
                        </div>
                    </div>
                </section>

                {/* Story */}
                <section className="py-12 md:py-20 lg:py-24">
                    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16 items-center">
                            <div>
                                <p className="text-xs tracking-[0.2em] uppercase mb-2 text-[#62B6CB] font-medium">Our Journey</p>
                                <h2 className="text-2xl md:text-3xl font-semibold text-[#1B4965] mb-6">Weaving Heritage into Modern Fashion</h2>
                                <p className="text-sm text-[#6B7280] leading-relaxed mb-4">
                                    NEELA was founded with a simple yet profound mission: to bring the beauty of India's diverse textile heritage to the modern wardrobe. We work directly with artisan communities across India, from the silk weavers of Varanasi to the block printers of Jaipur.
                                </p>
                                <p className="text-sm text-[#6B7280] leading-relaxed mb-4">
                                    Every saree, lehenga, and kurta in our collection tells a story of dedication, skill, and cultural pride. We believe that fashion should not only make you look beautiful but also support the hands that create it.
                                </p>
                                <p className="text-sm text-[#6B7280] leading-relaxed">
                                    Our commitment to quality means that each piece undergoes rigorous quality checks before reaching you. From fabric selection to final finishing, we ensure that every detail meets our exacting standards.
                                </p>
                            </div>
                            <div className="aspect-[4/5] overflow-hidden rounded-sm">
                                <img
                                    src="https://images.pexels.com/photos/2916387/pexels-photo-2916387.jpeg?auto=compress&cs=tinysrgb&w=800"
                                    alt="Our Craftsmanship"
                                    className="w-full h-full object-cover"
                                />
                            </div>
                        </div>
                    </div>
                </section>

                {/* Stats */}
                <section className="py-12 md:py-16 bg-[#1B4965]">
                    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8">
                            {stats.map((stat, i) => (
                                <div key={i} className="text-center">
                                    <p className="text-2xl md:text-3xl font-bold text-white mb-1">{stat.number}</p>
                                    <p className="text-xs md:text-sm text-white/60 font-light">{stat.label}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* Values */}
                <section className="py-12 md:py-20 lg:py-24 bg-[#F0F7FA]">
                    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="text-center mb-10 md:mb-14">
                            <p className="text-xs tracking-[0.2em] uppercase mb-2 text-[#62B6CB] font-medium">What We Stand For</p>
                            <h2 className="text-2xl md:text-3xl font-semibold text-[#1B4965]">Our Values</h2>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                            {values.map((value, i) => {
                                const Icon = value.icon;
                                return (
                                    <div key={i} className="bg-white p-6 rounded-sm border border-[#E5E7EB] text-center">
                                        <div className="w-12 h-12 bg-[#F0F7FA] rounded-full flex items-center justify-center mx-auto mb-4">
                                            <Icon className="w-6 h-6 text-[#1B4965]" />
                                        </div>
                                        <h3 className="text-sm font-semibold text-[#1A1A1A] mb-2">{value.title}</h3>
                                        <p className="text-xs text-[#6B7280] leading-relaxed">{value.description}</p>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </section>
            </main>

            <Footer />
        </div>
    );
};

export default About;
